import { useAppStore } from "@/store";
import { FC, PropsWithChildren, useCallback, useState } from "react";
import { DropItem } from "react-aria-components";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import dropZoneGraphicSvg from "@/assets/images/upload-dropzone.svg";
import { usePageDragAndDrop } from "@/components/drag-and-drop";
import { importDroppedItems } from "@/modules/importing/drop-and-drop-markdown";
import { EventContext } from "@/domains/metrics/context";

interface NotesPageDragAndDropProps extends PropsWithChildren {
  onDrag: (isDragging: boolean) => void;
  isEmpty: boolean;
}

export const NotesPageDragAndDrop: FC<NotesPageDragAndDropProps> = observer(
  ({ onDrag, isEmpty, children }) => {
    const { store } = useAppStore();
    const [isDragging, setIsDragging] = useState(false);
    const featureIsEnabled = store.spaceAccountFeatureFlags.getFlagStatus("markdown-import");

    const handleDropChange = useCallback(
      (isDragging: boolean) => {
        setIsDragging(isDragging);
        onDrag(isDragging);
      },
      [onDrag]
    );

    const handleDropEnter = useCallback(() => {
      if (store.uploadedFileBatches.aBatchIsUploading) return;
      handleDropChange(true);
    }, [store.uploadedFileBatches.aBatchIsUploading, handleDropChange]);

    const handleDropExit = useCallback(() => {
      handleDropChange(false);
    }, [handleDropChange]);

    const handleDrop = useCallback(
      (e: { items: DropItem[] }) => {
        handleDropChange(false);
        importDroppedItems(e.items, store, EventContext.NotesActions);
      },
      [handleDropChange, store]
    );

    usePageDragAndDrop({
      onDrop: handleDrop,
      onDropEnter: handleDropEnter,
      onDropExit: handleDropExit,
    });

    if (!featureIsEnabled) return children;

    return (
      <>
        <DropZoneWrapper>
          {children}
          {isDragging && !isEmpty && (
            <DragTargetShade>
              <DragTarget>
                <img src={dropZoneGraphicSvg} alt="Drop to add notes to Mem" />
                <DropZoneText>Drop to add notes to Mem</DropZoneText>
              </DragTarget>
            </DragTargetShade>
          )}
        </DropZoneWrapper>
      </>
    );
  }
);

const DropZoneWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  transition: all 0.2s ease;
`;

const DragTargetShade = styled.div`
  background-color: #ffffff99; /* 60% opacity white*/
  display: flex;
  margin: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.mediumLarge + "px"};
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const DragTarget = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.mblue.x400 + "0d"}; /* 5% opacity*/
  z-index: 10;
  font-size: 1.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropZoneText = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.mblue.x500};
  line-height: 1em;
  padding: 0;
  margin: 0;
`;
